import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/lib/motion";
import { SERVICES, IMAGES } from "@/data/content";

const groups = ["Signature Collection", "Specialist Therapies", "Express & Add-Ons", "Workplace"];

const Services = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      <PageHero eyebrow="Our Treatments" title="Services" image={IMAGES.massage}
        subtitle="Professional mobile massage & holistic therapy, tailored to you. Each treatment includes set-up, fresh linens and premium oils." />

      {groups.map((group) => {
        const items = SERVICES.filter((s) => s.group === group);
        if (!items.length) return null;
        return (
          <section key={group} className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-5xl text-primary mb-12 border-b border-border pb-6">{group}</h2>
            </Reveal>
            <div className="space-y-8">
              {items.map((s, i) => (
                <Reveal key={s.slug} delay={(i % 2) * 0.06}>
                  <article id={s.slug} className="scroll-mt-28 grid lg:grid-cols-12 gap-8 rounded-3xl border border-border bg-card overflow-hidden hover:shadow-lg transition-shadow duration-300">
                    <div className="lg:col-span-5 relative min-h-[260px]">
                      <img src={s.image} alt={s.name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <div className="lg:col-span-7 p-8 sm:p-10">
                      <p className="text-xs tracking-[0.2em] uppercase text-secondary font-semibold mb-2">{s.tagline}</p>
                      <h3 className="font-serif text-3xl sm:text-4xl text-primary mb-4">{s.name}</h3>
                      <p className="text-muted-foreground leading-relaxed mb-6">{s.description}</p>
                      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6 mb-6">
                        <div>
                          <p className="text-xs tracking-[0.15em] uppercase text-foreground/50 mb-2">Benefits</p>
                          <ul className="space-y-1.5">
                            {s.benefits.map((b) => (
                              <li key={b} className="flex items-start gap-2 text-sm text-foreground/80"><Check size={16} className="text-primary mt-0.5 shrink-0" />{b}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs tracking-[0.15em] uppercase text-foreground/50 mb-2">Duration & Pricing</p>
                          <ul className="space-y-1.5">
                            {s.durations.map((d) => <li key={d} className="text-sm text-foreground/80">{d}</li>)}
                          </ul>
                          <p className="text-xs tracking-[0.15em] uppercase text-foreground/50 mt-4 mb-1">Ideal For</p>
                          <p className="text-sm text-foreground/80">{s.idealFor}</p>
                        </div>
                      </div>
                      <Link to={`/services/${s.slug}`} data-testid={`service-${s.slug}`}
                        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3 font-semibold hover:bg-primary/90 transition-colors">
                        View treatment <ArrowUpRight size={17} />
                      </Link>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </section>
        );
      })}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="rounded-3xl bg-primary text-primary-foreground p-10 sm:p-16 text-center">
            <h2 className="font-serif text-3xl sm:text-5xl mb-4">Not sure which treatment is right for you?</h2>
            <p className="text-primary-foreground/80 max-w-xl mx-auto mb-8">Book a wellness consultation and we'll tailor the perfect session to your body and goals.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-8 py-4 font-semibold hover:bg-secondary/90 transition-colors">Get in touch <ArrowUpRight size={18} /></Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Services;
